import classNames from 'classnames';
import { useEffect, useMemo, type JSX } from 'react';
import { useForm, FieldValues, useWatch, Path, SubmitHandler } from 'react-hook-form';

import { Button, ButtonSizes, ConditionView, Icon } from '@/shared/ui';

import { generateFormFieldsProps } from './generateFormFieldsProps';
import { getComponent } from './getComponent';
import { FormFieldProps, Schema } from './types';

import './FormBuilder.scss';

interface FormBuilderProps<T extends FieldValues = FieldValues> {
  schema: Schema;
  className?: string;
  fieldClassName?: string;
  onSubmit: (data: T) => void;
}

export const FormBuilderPreview = <T extends FieldValues>({
  schema,
  className,
  fieldClassName,
  onSubmit,
}: FormBuilderProps<T>): JSX.Element => {
  const { handleSubmit, control, reset } = useForm<T>();

  const watchedValues = useWatch({ control });

  useEffect(() => {
    reset();
  }, [schema]);

  const formFieldsProps = useMemo<FormFieldProps[]>(() => {
    if (!schema) return [];
    return generateFormFieldsProps(schema, watchedValues) as FormFieldProps[];
  }, [schema, watchedValues]);

  const submitHandler: SubmitHandler<T> = (data) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)} className={classNames('form-builder-preview', className)}>
      <div className="form-builder-preview__fields-content">
        {formFieldsProps.map(({ show, ...fieldProps }) => {
          return (
            <ConditionView if={show} key={fieldProps.name}>
              {getComponent(
                {
                  ...fieldProps,
                  name: fieldProps.name as Path<T>,
                },
                control,
                fieldClassName,
              )}
            </ConditionView>
          );
        })}
      </div>
      <div className="form-builder-preview_submit-content">
        <Button size={ButtonSizes.LG} type="submit">
          Шукати <Icon name="arrow_forward" />
        </Button>
      </div>
    </form>
  );
};
